import React, { useState } from "react";
import axios from "axios";
import { backend } from "../../../backend";
import { logAxiosError } from "../../../logging/error";
import { useNavigate } from "react-router-dom";
import Checkbox from "../../../components/Checkbox/Checkbox";
import "./bookingPanel.css";
import { createSEOUrl } from "../../../utils/createSEOUrl";
import { validatePhoneNumber } from "../../../utils/validators";

const defaultFormData = {
  first_name: "",
  last_name: "",
  email: "",
  organization: "",
  mobile: "",
  message: "",
  news: false,
};

function BookingPanel({ ad }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(defaultFormData);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [serverError, setServerError] = useState("");
  const [invalidFields, setInvalidFields] = useState({
    message: false,
    email: false,
    mobile: false,
    organization: false,
    terms: false,
    first_name: false,
    last_name: false,
  });

  const onChangeHandler = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();
    setInvalidFields({});
    setServerError("");

    if (
      !formData.message ||
      !emailValidator(formData.email) ||
      !validatePhoneNumber(formData.mobile) ||
      !orgValidator(formData.organization) ||
      !formData.first_name ||
      !formData.last_name ||
      !acceptedTerms
    ) {
      setInvalidFields({
        message: !formData.message,
        email: !emailValidator(formData.email),
        mobile: !validatePhoneNumber(formData.mobile),
        organization: !orgValidator(formData.organization),
        first_name: !formData.first_name,
        last_name: !formData.last_name,
        terms: !acceptedTerms,
      });

      return;
    }

    try {
      const listingLink = `${
        window.location.origin
      }/lediga-lokaler/${createSEOUrl(ad.municipality)}/${createSEOUrl(
        ad.type_of_room,
      )}/${createSEOUrl(ad.address)}/${ad._id}`;

      const { data } = await axios.post(
        backend("/api/listings/show-interest"),
        {
          ad,
          listingLink,
          ...formData,
        },
      );

      if (data.status) {
        navigate("/intresseanmalan");
      }
    } catch (error) {
      logAxiosError(error);
      setServerError(error.response?.data?.msg);
    }
  };

  const emailValidator = (email) =>
    // eslint-disable-next-line
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      email,
    );

  const orgValidator = (organization) => {
    return /^[0-9]{6}-?[0-9]{4}$/.test(organization);
  };

  const orgNumberMasking = (e) => {
    const { value } = e.target;
    const cleanValue = value.replace(/[^0-9]/g, "");
    const sliced = cleanValue.slice(0, 10);
    const formatted =
      sliced.length > 6 ? `${sliced.slice(0, 6)}-${sliced.slice(6)}` : sliced;

    setFormData((prevData) => ({
      ...prevData,
      organization: formatted,
    }));
  };

  return (
    <div className="booking-panel">
      <h2 data-testid="booking-modal-header">Är du intresserad av lokalen?</h2>
      <h3>
        Få kontakt direkt med hyresvärden på{" "}
        {ad.address.charAt(0).toUpperCase() + ad.address.slice(1).toLowerCase()}{" "}
        för att hyra lokalen!
      </h3>
      <form id="booking-modal-form" onSubmit={onSubmitHandler}>
        <div className="booking-panel-input-row">
          <div>
            <label htmlFor="booking-email">E-post*</label>
            <input
              aria-required="true"
              aria-invalid={invalidFields.email ? "true" : "false"}
              aria-describedby={invalidFields.email ? "email-error" : undefined}
              type="text"
              name="email"
              id="booking-email"
              data-testid="booking-email"
              onChange={onChangeHandler}
              onBlur={() =>
                setInvalidFields((prev) => ({
                  ...prev,
                  email: !emailValidator(formData.email),
                }))
              }
              placeholder="E-postadress*"
              style={{ border: invalidFields.email ? "2px solid red" : "" }}
            />
            {invalidFields.email && (
              <span
                className="booking-modal-error"
                id="email-error"
                role="alert"
              >
                Vänligen ange en giltig e-postadress
              </span>
            )}
          </div>

          <div className="booking-panel-name">
            <div>
              <label htmlFor="booking-firstname">Förnamn*</label>
              <input
                aria-required="true"
                aria-invalid={invalidFields.first_name ? "true" : "false"}
                aria-describedby={
                  invalidFields.first_name ? "firstname-error" : undefined
                }
                type="text"
                name="first_name"
                id="booking-firstname"
                data-testid="booking-firstname"
                onChange={onChangeHandler}
                onBlur={() =>
                  setInvalidFields((prev) => ({
                    ...prev,
                    first_name: formData.first_name.trim() === "",
                  }))
                }
                placeholder="Förnamn*"
                style={{
                  border: invalidFields.first_name ? "2px solid red" : "",
                }}
              />
              {invalidFields.first_name && (
                <span
                  className="booking-modal-error"
                  id="firstname-error"
                  role="alert"
                >
                  Ange ett förnamn
                </span>
              )}
            </div>
            <div>
              <label htmlFor="booking-lastname">Efternamn*</label>
              <input
                aria-required="true"
                aria-invalid={invalidFields.last_name ? "true" : "false"}
                aria-describedby={
                  invalidFields.last_name ? "lastname-error" : undefined
                }
                type="text"
                name="last_name"
                id="booking-lastname"
                data-testid="booking-lastname"
                onChange={onChangeHandler}
                onBlur={() =>
                  setInvalidFields((prev) => ({
                    ...prev,
                    last_name: formData.last_name.trim() === "",
                  }))
                }
                placeholder="Efternamn*"
                style={{
                  border: invalidFields.last_name ? "2px solid red" : "",
                }}
              />
              {invalidFields.last_name && (
                <span
                  className="booking-modal-error"
                  id="lastname-error"
                  role="alert"
                >
                  Ange ett efternamn
                </span>
              )}
            </div>
          </div>
          <div className="booking-panel-name">
            <div>
              <label htmlFor="booking-organisation">
                Organisations nummer*
              </label>
              <input
                aria-required="true"
                aria-invalid={invalidFields.organization ? "true" : "false"}
                aria-describedby={
                  invalidFields.organization ? "org-nr-error" : undefined
                }
                type="text"
                name="organization"
                id="booking-organisation"
                data-testid="booking-organisation"
                onChange={orgNumberMasking}
                value={formData.organization}
                placeholder="Org.nr*"
                onBlur={() =>
                  setInvalidFields((prev) => ({
                    ...prev,
                    organization: !orgValidator(formData.organization),
                  }))
                }
                style={{
                  border: invalidFields.organization ? "2px solid red" : "",
                }}
              />
              {invalidFields.organization && (
                <span
                  className="booking-modal-error"
                  id="org-nr-error"
                  role="alert"
                >
                  Org.nr måste vara i formatet XXXXXX-XXXX
                </span>
              )}
            </div>
            <div>
              <label htmlFor="booking-phone">Telefonnummer*</label>
              <input
                aria-required="true"
                type="tel"
                inputMode="tel"
                name="mobile"
                id="booking-phone"
                data-testid="booking-phone"
                onChange={onChangeHandler}
                placeholder="Telefon*"
                onBlur={() => {
                  setInvalidFields((prev) => ({
                    ...prev,
                    mobile: !validatePhoneNumber(formData.mobile),
                  }));
                }}
                aria-invalid={invalidFields.mobile ? "true" : "false"}
                aria-describedby={
                  invalidFields.mobile ? "mobile-error" : undefined
                }
                style={{ border: invalidFields.mobile ? "2px solid red" : "" }}
              />

              {invalidFields.mobile && (
                <span
                  className="booking-modal-error"
                  id="mobile-error"
                  role="alert"
                >
                  Vänligen ange ett giltigt telefonnummer
                </span>
              )}
            </div>
          </div>
          <div className="booking-modal-message">
            <label htmlFor="booking-modal-add-message-input">Meddelande*</label>
            <textarea
              aria-required="true"
              aria-invalid={invalidFields.message ? "true" : "false"}
              aria-describedby={
                invalidFields.message ? "message-error" : undefined
              }
              value={formData.message}
              onChange={onChangeHandler}
              name="message"
              id="booking-modal-add-message-input"
              data-testid="booking-msg-input"
              cols="30"
              rows="8"
              maxLength="2000"
              placeholder="Beskriv ditt behov så utförligt som möjligt*"
              style={{ border: invalidFields.message ? "2px solid red" : "" }}
              // onInput={autoExpand}
              onBlur={() =>
                setInvalidFields((prev) => ({
                  ...prev,
                  message: formData.message.trim() === "",
                }))
              }
            />
            {invalidFields.message && (
              <span
                className="booking-modal-error"
                id="message-error"
                role="alert"
              >
                Meddelandet får inte vara tomt
              </span>
            )}
          </div>
        </div>
        {serverError && <p className="booking-modal-error">{serverError}</p>}
        <div className="checkbox-speciffic">
          <Checkbox
            aria-required="true"
            aria-invalid={invalidFields.terms ? "true" : "false"}
            aria-describedby={
              invalidFields.terms ? "terms-error-msg" : undefined
            }
            data-testid="booking-terms-checkbox"
            name="terms"
            checked={acceptedTerms}
            onChange={() => {
              setAcceptedTerms((prev) => {
                const newValue = !prev;
                setInvalidFields((fields) => ({
                  ...fields,
                  terms: !newValue,
                }));
                return newValue;
              });
            }}
            label={
              <p>
                Härmed bekräftar jag att jag har läst och godkänner{" "}
                <a
                  href="/anvandarvillkor"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  användarvillkoren
                </a>
                , inklusive att avtalsvite kan utgå vid bristande återkoppling
                eller kringgående av plattformen.*
              </p>
            }
          />

          {invalidFields.terms && (
            <span
              className="booking-modal-error"
              role="alert"
              id="terms-error-msg"
            >
              Du måste acceptera villkoren
            </span>
          )}
          <div className="news-checkbox">
            <Checkbox
              name="news"
              checked={formData.news}
              onChange={() =>
                setFormData((prev) => ({ ...prev, news: !prev.news }))
              }
              label={
                <p>Jag vill ta emot nyheter och erbjudanden från Vakansa.</p>
              }
            />
          </div>
        </div>

        <div className="booking-modal-buttons">
          <button type="submit" data-testid="booking-send-btn">
            Skicka förfrågan
          </button>
          <p>
            Vänligen respektera hyresvärdens önskemål om att all kontakt sker
            genom Vakansa.
          </p>
        </div>
      </form>
    </div>
  );
}

export default BookingPanel;
